import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "./AuthContext";
import { ThemeContextProvider } from "./contexts/ThemeContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { LocaleProvider } from "./contexts/LocaleContext";
import { QueryProvider } from "./contexts/QueryProvider";
import AppLayout from "./components/layout/AppLayout";
import HomePage from "./components/pages/HomePage";
import SearchPage from "./components/pages/SearchPage";
import DashboardPage from "./components/pages/DashboardPage";
import SettingsPage from "./components/pages/SettingsPage";
import NotificationsPage from "./components/pages/NotificationsPage";
import LostItemList from "./components/LostItemList";
import LostItemDetail from "./components/LostItemDetail";
import LostItemForm from "./components/LostItemForm";
import LostItemEditForm from "./components/LostItemEditForm";
import AdminUsersList from "./components/AdminUsersList";
import StoreManagement from "./components/StoreManagement";
import StoreSelectDemo from "./components/StoreSelectDemo";
import LostItemAutoRegisterPage from "./components/LostItemAutoRegisterPage";
import InquiriesPage from "./components/pages/InquiriesPage";
import InquiryChatPage from "./components/pages/InquiryChatPage";
import LoginPage from "./components/pages/LoginPage";
import PrivateRoute from "./components/PrivateRoute";
import ProfilePage from "./components/pages/ProfilePage";

function AppContent() {
  const { currentUser, loading } = useAuth();

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            loading ? null : currentUser ? <Navigate to="/" /> : <LoginPage />
          }
        />

        {/* Public and Private Routes within AppLayout */}
        <Route
          path="/*"
          element={
            <AppLayout>
              <Routes>
                {/* Public Routes - 認証不要なので即座に表示 */}
                <Route path="/" element={<HomePage />} />
                <Route path="/items" element={<LostItemList />} />
                <Route path="/items/:id" element={<LostItemDetail />} />
                <Route path="/search" element={<SearchPage />} />

                {/* Private Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <DashboardPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <SettingsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <PrivateRoute>
                      <NotificationsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/inquiries"
                  element={
                    <PrivateRoute>
                      <InquiriesPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/inquiries/:inquiryId"
                  element={
                    <PrivateRoute>
                      <InquiryChatPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <PrivateRoute>
                      <LostItemForm />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/auto-register"
                  element={
                    <PrivateRoute>
                      <LostItemAutoRegisterPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/items/:id/edit"
                  element={
                    <PrivateRoute>
                      <LostItemEditForm />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <PrivateRoute>
                      <AdminUsersList />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/stores"
                  element={
                    <PrivateRoute>
                      <StoreManagement />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/demo/store-select"
                  element={
                    <PrivateRoute>
                      <StoreSelectDemo />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <ProfilePage />
                    </PrivateRoute>
                  }
                />

                {/* Catch-all for any other routes */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </AppLayout>
          }
        />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeContextProvider>
      <LocaleProvider>
        <NotificationProvider>
          <QueryProvider>
            <AppContent />
          </QueryProvider>
        </NotificationProvider>
      </LocaleProvider>
    </ThemeContextProvider>
  );
}

export default App;
